import { useEffect, useMemo, useState, useRef, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import Layout from "@/components/Layout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { TrendingUp, TrendingDown, Minus, AlertTriangle, Users, CalendarRange, Trophy, GraduationCap, Rocket, CalendarDays, UserCheck } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { toast } from "sonner";

const classLabel = (c) => {
  if (c === "LKG" || c === "UKG") return c;
  const n = parseInt(c, 10);
  if (isNaN(n)) return c;
  const s = ["th","st","nd","rd"], v = n % 100;
  return `${n}${s[(v-20)%10] || s[v] || s[0]}`;
};

export default function Analytics({ embedded } = {}) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [meta, setMeta] = useState({ assignments: [] });
  const [cn, setCn] = useState(localStorage.getItem("sa_selected_class") || "");
  const [sec, setSec] = useState(localStorage.getItem("sa_selected_section") || "");
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [view, setView] = useState(searchParams.get("view") === "marks" ? "marks" : "attendance");   // "attendance" | "marks"
  const [marks, setMarks] = useState(null);
  const [marksLoading, setMarksLoading] = useState(false);

  const allClasses = cn === "__ALL__";

  // "All classes" only applies to the attendance view; leaving it selects a real class.
  useEffect(() => {
    if (view === "marks" && allClasses) {
      const first = meta.assignments?.[0];
      if (first) { setCn(first.class_name); setSec(first.section); }
    }
  }, [view, allClasses, meta]);

  useEffect(() => {
    if (view !== "marks" || !cn || !sec || allClasses) return;
    setMarksLoading(true);
    api.get("/teacher/marks-detail", { params: { class_name: cn, section: sec } })
      .then(({ data }) => setMarks(data))
      .catch((e) => toast.error(apiErr(e.response?.data?.detail) || "Could not load marks"))
      .finally(() => setMarksLoading(false));
  }, [view, cn, sec, allClasses]);

  useEffect(() => {
    api.get("/meta/classes").then(({ data }) => setMeta(data));
  }, []);

  // One-time default selection once user (role) + class registry are both ready.
  const didDefault = useRef(false);
  useEffect(() => {
    if (didDefault.current || !user || !(meta.assignments || []).length) return;
    didDefault.current = true;
    if (isAdmin) {
      // Admins default to the school-wide "All classes" view on load.
      setCn("__ALL__");
    } else if (!cn) {
      setCn(meta.assignments[0].class_name);
      setSec(meta.assignments[0].section);
    }
    // eslint-disable-next-line
  }, [user, meta]);

  useEffect(() => {
    if (!cn || (!sec && !allClasses)) return;
    setLoading(true);
    api.get("/analytics/overview", { params: { class_name: cn, section: allClasses ? "" : sec, days } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiErr(e.response?.data?.detail) || "Could not load analytics"))
      .finally(() => setLoading(false));
  }, [cn, sec, days, allClasses]);

  const validPairs = meta.assignments || [];
  // Sections available for the currently selected class
  const availableSections = useMemo(
    () => validPairs.filter((a) => a.class_name === cn).map((a) => a.section).sort((a, b) => String(a).localeCompare(String(b))),
    [validPairs, cn]
  );
  const CLASS_ORDER = ["Play Group", "LKG", "UKG", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
  const availableClasses = useMemo(
    () => [...new Set(validPairs.map((a) => a.class_name))].sort(
      (a, b) => (CLASS_ORDER.indexOf(a) === -1 ? 99 : CLASS_ORDER.indexOf(a)) - (CLASS_ORDER.indexOf(b) === -1 ? 99 : CLASS_ORDER.indexOf(b))
    ),
    [validPairs]
  );

  const maxBar = data?.daily?.length
    ? Math.max(...data.daily.map((d) => d.total)) || 1
    : 1;

  const Wrap = embedded ? Fragment : Layout;

  return (
    <Wrap>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        {/* Attendance / Marks tab switcher */}
        <div className="inline-flex rounded-full border border-slate-200 dark:border-slate-700 p-1" data-testid="analytics-tabs">
          {[
            { key: "attendance", label: "Attendance", icon: CalendarDays },
            { key: "marks", label: "Marks", icon: GraduationCap },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setView(t.key)}
              data-testid={`analytics-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 h-9 rounded-full text-sm font-medium transition-colors ${
                view === t.key ? "bg-sky-500 text-white shadow-sm" : "text-slate-500 dark:text-slate-300 hover:text-slate-700 dark:hover:text-slate-100"
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Class</label>
            <Select value={cn} onValueChange={(v) => { setCn(v); if (v === "__ALL__") return; const first = validPairs.find((a) => a.class_name === v); if (first) setSec(first.section); }}>
              <SelectTrigger className="h-10 rounded-xl w-44" data-testid="analytics-class-select"><SelectValue placeholder="Select"/></SelectTrigger>
              <SelectContent>
                {isAdmin && view === "attendance" && (
                  <SelectItem value="__ALL__" data-testid="analytics-class-all">All classes</SelectItem>
                )}
                {availableClasses.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`analytics-class-${c}`}>{classLabel(c)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Section</label>
            <Select value={sec} onValueChange={setSec} disabled={allClasses}>
              <SelectTrigger className="h-10 rounded-xl w-32" data-testid="analytics-section-select"><SelectValue placeholder={allClasses ? "All" : "Select"}/></SelectTrigger>
              <SelectContent>
                {availableSections.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`analytics-section-${s}`}>Sec {s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {view === "attendance" && (
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Window</label>
              <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
                <SelectTrigger className="h-10 rounded-xl w-32" data-testid="analytics-days-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="60">Last 60 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {/* Title below the tabs, smaller */}
      <div className="mb-6">
        <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>
          {view === "attendance" ? "Attendance trends & at-risk" : "Marks & performance"}
        </h1>
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 mt-0.5">
          <p className="text-xs text-slate-500">
            {view === "attendance" ? "Spot patterns early and follow up with parents." : "Class averages, rankings and biggest improvers."}
          </p>
          {view === "marks" && marks?.exam_label && (
            <span className="text-xs text-slate-400" data-testid="marks-showing-label">
              · Showing <span className="font-semibold text-slate-500 dark:text-slate-300">{marks.exam_label}</span>{marks.prev_exam_label ? ` · compared to ${marks.prev_exam_label}` : ""}
            </span>
          )}
        </div>
      </div>

      {view === "marks" ? (
        <MarksView marks={marks} loading={marksLoading} hasClass={!!(cn && sec)} validPairs={validPairs} />
      ) : (() => {
        if (validPairs.length === 0) {
          return <EmptyCard text="No classes assigned to you. Ask your admin to grant access." />;
        }
        if (!cn || (!sec && !allClasses)) {
          return <EmptyCard text="Pick a class and section to see analytics." />;
        }
        if (loading) {
          return <EmptyCard text="Loading analytics…" />;
        }
        if (!data) {
          return null;
        }
        if (data.daily.length === 0) {
          return <EmptyCard text="No attendance has been submitted yet for this class in the selected window." />;
        }
        return (
        <div className="space-y-6" data-testid="analytics-dashboard">
          {/* Summary tiles */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Stat icon={TrendingUp} label="Avg. attendance" value={`${data.summary.avg_percentage}%`} tone="sky" testid="stat-avg-pct" />
            <Stat icon={Users} label="Enrolled" value={data.enrolled} tone="slate" />
            <Stat icon={CalendarRange} label="Days recorded" value={data.summary.days_recorded} tone="slate" />
            <Stat icon={AlertTriangle} label="At-risk (<75%)" value={data.summary.at_risk_count} tone="rose" testid="stat-at-risk" />
            <Stat icon={Trophy} label="Top performer" value={data.top_present[0] ? `${data.top_present[0].percentage}%` : "—"} tone="emerald" />
          </div>

          {/* Daily attendance trend */}
          <AttendanceTrendChart daily={data.daily} days={data.days} />

          {/* At risk students */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="at-risk-list">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center">
                  <AlertTriangle className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-900" style={{fontFamily:"Outfit"}}>At-risk students</div>
                  <div className="text-xs text-slate-500">Below 75% attendance in the window</div>
                </div>
              </div>
              <span className="text-xs text-slate-500">{data.at_risk.length} flagged</span>
            </div>
            {data.at_risk.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">No at-risk students. Great job!</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.at_risk.map((s) => (
                  <li key={s.student_id} className="flex items-center gap-3 px-5 py-3" data-testid={`at-risk-${s.roll_number}`}>
                    <div className="h-9 w-9 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-sm font-medium">
                      {s.name.split(" ").map((p) => p[0]).slice(0,2).join("")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{s.name}</div>
                      <div className="text-xs text-slate-500">Roll #{s.roll_number} · {s.present}/{s.total} days present</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-rose-600">{s.percentage}%</div>
                      <div className="w-24 h-1.5 rounded-full bg-slate-100 mt-1 overflow-hidden">
                        <div className="h-full bg-rose-400" style={{width: `${Math.min(100, s.percentage)}%`}}/>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        );
      })()}
    </Wrap>
  );
}

function MarksView({ marks, loading, hasClass, validPairs }) {
  if (validPairs.length === 0) return <EmptyCard text="No classes assigned to you. Ask your admin to grant access." />;
  if (!hasClass) return <EmptyCard text="Pick a class and section to see marks analytics." />;
  if (loading || !marks) return <EmptyCard text="Loading marks…" />;
  if (!marks.exam_label || !(marks.ranking || []).length) {
    return <EmptyCard text="No exam results published yet for this class." />;
  }

  const pcts = marks.ranking.map((r) => r.percentage).filter((p) => p != null);
  const classAvg = pcts.length ? Math.round((pcts.reduce((a, b) => a + b, 0) / pcts.length) * 10) / 10 : null;
  const top = marks.ranking[0];
  const improver = (marks.improvement || []).find((s) => s.jump > 0);
  const passCount = pcts.filter((p) => p >= 35).length;
  const passRate = pcts.length ? Math.round((passCount / pcts.length) * 100) : null;

  return (
    <div className="space-y-6" data-testid="marks-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={GraduationCap} label="Class average" value={classAvg == null ? "—" : `${classAvg}%`} tone="sky" testid="marks-stat-avg" />
        <Stat icon={TrendingUp} label="Pass rate (≥35%)" value={passRate == null ? "—" : `${passRate}%`} tone="emerald" />
        <Stat icon={Trophy} label="Top scorer" value={top ? `${top.percentage}%` : "—"} tone="emerald" />
        <Stat icon={Rocket} label="Most improved" value={improver ? `+${improver.jump}%` : "—"} tone="slate" />
      </div>

      {/* Subject averages */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5" data-testid="marks-subject-averages">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>Subject averages</div>
          {marks.class_teacher && (
            <div className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 px-3 py-1 text-xs font-medium" data-testid="marks-class-teacher">
              <UserCheck className="h-3.5 w-3.5" />
              Class teacher: <span className="font-semibold">{marks.class_teacher}</span>
            </div>
          )}
        </div>
        <div className="space-y-3">
          {(marks.subjects || []).map((s) => {
            const weak = s.avg_pct != null && s.avg_pct < 50;
            return (
              <div key={s.subject} className="flex items-center gap-3" data-testid={`marks-subject-${s.subject}`}>
                <div className="w-28 shrink-0 text-sm text-slate-700 dark:text-slate-200 truncate font-medium">{s.subject}</div>
                <div className="flex-1 h-3 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                  <div className={`h-full rounded-full ${weak ? "bg-rose-400" : "bg-sky-500"}`} style={{ width: `${s.avg_pct ? Math.min(100, s.avg_pct) : 0}%` }} />
                </div>
                <div className={`w-12 text-right text-sm font-semibold ${weak ? "text-rose-500" : "text-slate-700 dark:text-slate-200"}`}>{s.avg_pct == null ? "—" : `${s.avg_pct}%`}</div>
                <div className="w-28 sm:w-36 shrink-0 text-xs text-slate-400 dark:text-slate-500 truncate" title={s.teacher || ""} data-testid={`marks-subject-teacher-${s.subject}`}>{s.teacher || "—"}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Ranking */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden" data-testid="marks-ranking">
          <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-amber-50 dark:bg-amber-900/40 text-amber-600 dark:text-amber-300 flex items-center justify-center"><Trophy className="h-4 w-4" /></div>
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>Class ranking</div>
          </div>
          <ul className="divide-y divide-slate-100 dark:divide-slate-800 max-h-96 overflow-y-auto">
            {marks.ranking.map((r) => (
              <li key={r.student_id} className="flex items-center gap-3 px-5 py-2.5" data-testid={`marks-rank-${r.rank}`}>
                <span className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-[11px] font-bold ${r.rank===1?"bg-amber-100 text-amber-700":r.rank===2?"bg-slate-200 text-slate-600":r.rank===3?"bg-orange-100 text-orange-700":"bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-300"}`}>{r.rank}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{r.name}</div>
                  <div className="text-[11px] text-slate-400 font-mono">#{r.roll_number}</div>
                </div>
                <TrendIcon trend={r.trend} />
                <span className={`text-sm font-semibold w-12 text-right ${r.percentage != null && r.percentage < 50 ? "text-rose-500 dark:text-rose-400" : "text-amber-600 dark:text-amber-300"}`}>{r.percentage != null ? `${r.percentage}%` : "—"}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Improvement */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden" data-testid="marks-improvement">
          <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300 flex items-center justify-center"><Rocket className="h-4 w-4" /></div>
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>Progress vs {marks.prev_exam_label || "previous exam"}</div>
          </div>
          {(marks.improvement || []).length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">Progress appears once a second exam is published.</div>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800 max-h-96 overflow-y-auto">
              {marks.improvement.map((s) => (
                <li key={s.student_id} className="flex items-center gap-3 px-5 py-2.5" data-testid={`marks-improve-${s.roll_number}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{s.name}</div>
                    <div className="text-[11px] text-slate-400">{s.prev_percentage}% → <span className="text-slate-600 dark:text-slate-300 font-medium">{s.percentage}%</span></div>
                  </div>
                  <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${s.jump > 0 ? "text-emerald-600 dark:text-emerald-300" : s.jump < 0 ? "text-rose-500" : "text-slate-400"}`}>
                    {s.jump > 0 ? <TrendingUp className="h-3.5 w-3.5" /> : s.jump < 0 ? <TrendingDown className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
                    {s.jump > 0 ? "+" : ""}{s.jump}%
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function TrendIcon({ trend }) {
  if (trend === "up") return <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />;
  if (trend === "down") return <TrendingDown className="h-3.5 w-3.5 text-rose-500" />;
  if (trend === "same") return <Minus className="h-3.5 w-3.5 text-slate-400" />;
  if (trend === "new") return <span className="text-[10px] uppercase text-sky-500">new</span>;
  return <span className="w-3.5" />;
}

function Stat({ icon: Icon, label, value, tone = "slate", testid }) {
  const map = {
    slate: "bg-white border-slate-200 text-slate-700",
    sky: "bg-sky-50 border-sky-200 text-sky-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  };
  return (
    <div className={`rounded-2xl border p-4 ${map[tone]}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs uppercase tracking-[0.2em] opacity-70">{label}</div>
        <Icon className="h-4 w-4 opacity-70" />
      </div>
      <div className="text-2xl font-bold" style={{fontFamily:"Outfit"}}>{value}</div>
    </div>
  );
}

function AttendanceTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg px-3 py-2 text-xs">
      <div className="font-semibold text-slate-800 dark:text-slate-100 mb-1">{d.full}</div>
      <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
        <span className="h-2 w-2 rounded-full bg-sky-500" /> Attendance <b className="ml-1">{d.pct}%</b>
      </div>
      <div className="text-slate-500 mt-0.5">Morning: {d.amP}/{d.amT} · Afternoon: {d.pmP}/{d.pmT}</div>
    </div>
  );
}

function AttendanceTrendChart({ daily, days }) {
  const chartData = daily.map((d) => {
    const m = d.morning || { present: 0, absent: 0 };
    const a = d.afternoon || { present: 0, absent: 0 };
    let full = d.date;
    try { full = new Date(d.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }); } catch { /* noop */ }
    return {
      label: d.date.slice(5),
      full,
      pct: d.percentage,
      amP: m.present, amT: m.present + m.absent,
      pmP: a.present, pmT: a.present + a.absent,
    };
  });
  // keep x-axis readable — show ~10 labels max
  const step = Math.max(0, Math.ceil(chartData.length / 10) - 1);

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5" data-testid="analytics-chart">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>Attendance trend</div>
          <div className="text-xs text-slate-500">% present each day · last {days} days</div>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-4 rounded-sm bg-sky-400" /> Daily %</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-0 w-4 border-t-2 border-dashed border-rose-400" /> 75% at-risk</span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={chartData} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="attFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.28} />
              <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis dataKey="label" interval={step} tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
          <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} width={46} />
          <Tooltip content={<AttendanceTooltip />} />
          <ReferenceLine y={75} stroke="#fb7185" strokeDasharray="5 4" strokeWidth={1.5} />
          <Area type="monotone" dataKey="pct" stroke="#0ea5e9" strokeWidth={2.5} fill="url(#attFill)" dot={{ r: 2, fill: "#0ea5e9" }} activeDot={{ r: 5 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function EmptyCard({ text }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-500" data-testid="analytics-empty">
      {text}
    </div>
  );
}
